// InboxFilters (M1 §8): tipo, centro (dos `contexts[]` de `/me`, "Rede" só com `hasNetworkAccess`), atrasados.
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { useMe } from '../../../lib/auth/authContext';
import type { WorkItemKind } from '../api/contract.preview';

export interface InboxFiltersValue {
  readonly kind: WorkItemKind | 'all';
  /** Um `tenantId`, ou o sentinela `'all'`/`'network'` (a união colapsaria em `string`, por isso o comentário). */
  readonly tenantId: string;
  readonly overdue: boolean;
}

export interface InboxFiltersProps {
  readonly value: InboxFiltersValue;
  readonly onChange: (value: InboxFiltersValue) => void;
}

export function InboxFilters({ value, onChange }: InboxFiltersProps) {
  const { t } = useTranslation('workflow');
  const me = useMe();

  return (
    <fieldset className="flex flex-wrap items-end gap-3" data-testid="inbox-filters">
      <legend className="sr-only">{t('inbox.filters.title')}</legend>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="inbox-filter-kind"
          className="text-caption font-medium text-muted-foreground"
        >
          {t('inbox.filters.kind')}
        </label>
        <Select
          value={value.kind}
          onValueChange={(next) => {
            onChange({ ...value, kind: next as InboxFiltersValue['kind'] });
          }}
        >
          <SelectTrigger
            id="inbox-filter-kind"
            className="w-44"
            aria-label={t('inbox.filters.kind')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inbox.filters.allKinds')}</SelectItem>
            <SelectItem value="task">{t('kinds.task')}</SelectItem>
            <SelectItem value="approval">{t('kinds.approval')}</SelectItem>
            <SelectItem value="exception">{t('kinds.exception')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="inbox-filter-tenant"
          className="text-caption font-medium text-muted-foreground"
        >
          {t('inbox.filters.tenant')}
        </label>
        <Select
          value={value.tenantId}
          onValueChange={(next) => {
            onChange({ ...value, tenantId: next });
          }}
        >
          <SelectTrigger
            id="inbox-filter-tenant"
            className="w-52"
            aria-label={t('inbox.filters.tenant')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inbox.filters.allTenants')}</SelectItem>
            {me.networkAccess.hasAccess && (
              <SelectItem value="network">{t('inbox.filters.network')}</SelectItem>
            )}
            {me.contexts.map((context) => (
              <SelectItem key={context.tenantId} value={context.tenantId}>
                {context.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant={value.overdue ? 'default' : 'outline'}
        aria-pressed={value.overdue}
        onClick={() => {
          onChange({ ...value, overdue: !value.overdue });
        }}
      >
        {t('inbox.filters.overdue')}
      </Button>
    </fieldset>
  );
}
